import {Component, OnInit} from '@angular/core';
import {StepComponent} from '../../step/step.component';
import {EingabeForm} from './eingabe.form';
import {Step, StepService} from '../../services/step.service';
import {ActivatedRoute, Router} from '@angular/router';
import {ElementService} from '../../services/element.service';
import {PortalService} from '../../services/portal.service';
import {WarmwassercheckAnfrage} from "../anfrage/warmwassercheck.anfrage";
import {WarmwassercheckRequestService} from "../services/request.service";
import {WarmwassercheckConstants} from "../warmwassercheck.constants";

/**
 * Overwrite the Generic step component for the first section of the warmwassercheck.
 *
 * @inheritDoc
 */
@Component({
    selector: 'app-step-eingabe-verbrauchsdaten',
    templateUrl: '../../step/step.component.html',
    standalone: false
})
export class EingabeStepComponent extends StepComponent implements OnInit {

  /**
   * (input) data of the user
   */
  anfrage: WarmwassercheckAnfrage;

  /**
   * @inheritDoc
   */
  formConfig: EingabeForm;

  /**
   * Overwrite the label prefix.
   */
  textkeyPrefix = 'warmwassercheck';

  /**
   * textkey will be set in case of an error
   */
  formError: string;

  /**
   * overwrite textkey for the help on the right side
   */
  helpTextkey: string = null;

  /**
   * holds the path to the correct result page
   */
  pathResultPage: string = null;

  /**
   * set to true if nutzungstyp is mehrfamilienhaus
   */
  isMfh: boolean = false;

  /**
   * set to true if warmwasser_bereitungstyp is gebaeudezentral
   */
  isGebaeudezentral: boolean = false;

  /**
   * set to true if warmwasser_bereitungstyp is wohnungszentral
   */
  isWohnungszentral: boolean = false;

  /**
   * set to true if last step is in consumption path
   */
  isLastStepConsumption: boolean = false;

  /**
   * set to true if last step is in estimation path
   */
  isLastStepEstimation: boolean = false;

  /**
   * set to true if last step of geraetedaten
   */
  isLastStepGeraetedaten: boolean = false;

  /**
   * set to true if current step is geraetedaten/2
   */
  isStepGeraetedaten2: boolean = false;

  /**
   * set to true for mieter/weg in mfh with gebaeudezentral
   */
  isEarlyResult: boolean = false;

  /**
   * set to true for warmwasser_bereitungstyp wohungszentral or dezentral
   */
  isWohnungsOrDezentralResult: boolean = false;

  /**
   * holds the name of the current step
   */
  currentStepName: string;

  /**
   * holds the path to the next step in case of a 'consumption too high' warning
   */
  pathNextStep: string = null;

  /**
   * @inheritDoc
   */
  constructor(protected stepService: StepService, protected router: Router, protected requestService: WarmwassercheckRequestService,
              protected elementService: ElementService, protected portalService: PortalService, protected route: ActivatedRoute) {
    super(stepService, router, elementService);
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    // add the correct form config that we need in this steps and init the parent form
    this.formConfig = new EingabeForm();
    super.ngOnInit();

    this.anfrage = this.requestService.getAnfrage();
    this.formConfig.anfrage = this.anfrage;

    this.formConfig.setStepComponent(this);
    this.formConfig.setData(this.anfrage.userInput);

    // define some variables
    if (this.steps?.currentStep && this.anfrage) {
      this.currentStepName = this.steps?.currentStep?.name;
      this.isMfh = this.anfrage?.userInput?.gebaeudetyp === WarmwassercheckConstants.GEBAEUDETYP_MFH;
      this.isGebaeudezentral = this.anfrage?.userInput?.warmwasser_bereitungstyp === WarmwassercheckConstants.WARMWASSER_BEREITUNGSTYP_GEBAEUDEZENTRAL;
      this.isWohnungszentral = this.anfrage?.userInput?.warmwasser_bereitungstyp === WarmwassercheckConstants.WARMWASSER_BEREITUNGSTYP_WOHNUNGSZENTRAL;
      this.isLastStepConsumption = this.steps?.currentStep?.name === WarmwassercheckConstants.LAST_STEP_OF_CONSUMPTION_BEKANNT;
      this.isLastStepEstimation = this.steps?.currentStep?.name === WarmwassercheckConstants.LAST_STEP_OF_CONSUMPTION_GESCHAETZT;
      this.isLastStepGeraetedaten = this.steps?.currentStep?.name === WarmwassercheckConstants.LAST_STEP_OF_GERAETEDATEN;
      this.isStepGeraetedaten2 = this.steps?.currentStep?.name === WarmwassercheckConstants.STEP_GERAETEDATEN_2;
    }

    // check if we have early result and set to true
    this.isEarlyResult = this.isMfh && this.isGebaeudezentral && (this.isLastStepConsumption || this.isLastStepEstimation);
    this.isWohnungsOrDezentralResult = !this.isGebaeudezentral && this.isStepGeraetedaten2;

    // update the previous step
    this.onStepUpdate();

    // set the next step to correct result page
    this.setResultSteps();

    // if there are changes in the form, reset the warnings
    this.subscriptions.push(this.form.valueChanges.subscribe(() => {
      this.formWarning = null;
    }));

    // update navigation dots to link to correct step
    this.updateNavigation();
  }

  /**
   * @inheritDoc
   */
  saveData(data: { [key: string]: any }) {
    // merge the data with the existing data
    this.anfrage.userInput = {...this.anfrage.userInput, ...data};
    this.requestService.setAnfrage(this.anfrage);

    // save the data also to the server
    this.requestService.saveDataToServer();

    super.saveData(data);
  }

  /**
   * moveon to the next step in the routing
   */
  moveToNextStep(): void {
    if (this.steps?.currentStep && this.anfrage) {
      // consumption/estimation with next steps: get result after consumption/estimation input to display possible warning
      if ((this.isLastStepConsumption || this.isLastStepEstimation) && !this.isEarlyResult) {
        // continue to the next step setting correct path
        this.pathNextStep = WarmwassercheckConstants.PATH_EINGABE + (
          this.isWohnungszentral ? WarmwassercheckConstants.STEP_GERAETEDATEN_1 : WarmwassercheckConstants.STEP_WASSERBEREITUNG_1
        );
        this.getResult();
        return;
      }

      // result page: get result on last step or when we have early result for mieter/wohnungseigentuemer in mfh (inputs to geraetedaten not asked)
      if (this.isEarlyResult || this.isLastStepGeraetedaten || this.isWohnungsOrDezentralResult) {
        this.pathResultPage = WarmwassercheckConstants.PATH_ERGEBNIS;
        this.getResult();
        return;
      }
    }

    super.moveToNextStep();
  }

  /**
   * Return the next step from a step list
   */
  getNextStepFromList(stepList: Step[]): any {
    // set all data again in the form to avoid using old data for actual step
    if (this.anfrage?.userInput && this.formConfig) {
      this.formConfig.updateFields(this.anfrage.userInput);
      // save the data again. in some cases some fields are auto updated
      this.saveData({});
    }
    return super.getNextStepFromList(stepList);
  }

  /**
   * @inheritDoc
   */
  onSubmit() {
    super.onSubmit();

    // check if we end the advisor
    if(this.isAdvisorEnd()) {
      // save data
      this.saveData(this.formConfig.getData());

      // navigate to end page
      this.router.navigateByUrl(
        this.portalService.currentPortalId + "/warmwassercheck/eingabe/ausstieg",
        {state: {lastStep: this.router.url}}
      );
      return;
    }

    if (this.form.valid === false) {
      // form is not valid, maybe form errors are shown. Scroll upwards
      this.elementService.scrollToElement(this.stepForm.nativeElement);
    }
  }

  /**
   * @inheritDoc
   */
  onStepUpdate() {
    super.onStepUpdate();

    // set previous step to first starting page
    if(this.currentStepName === WarmwassercheckConstants.FIRST_STEP_OF_INPUT) {
      this.previousStep = new Step('start', WarmwassercheckConstants.PATH_STARTING_PAGE);
    }

    // overwrite help textekys
    this.initHelptextkeys();
  }

  /**
   * get the result from the backend and throw form error if there is no result
   */
  getResult(): void {
    // start loading animation
    this.loading = true;

    // get current data from last step and check if we must reset something
    this.saveData(this.formConfig.getData());
    this.formConfig.updateFields(this.anfrage.userInput);

    // send data to backend
    this.requestService.backendService.getRequestId().subscribe(requestId => {

        // recalculate hot water
        this.requestService.backendService.result(this.anfrage, requestId).subscribe(data => {
          // save the result data in anfrage
          this.anfrage.result = data;
          // check if we have a warning backend for too high water consumption
          if ((data.result?.status?.warnings?.length > 0 &&
            !this.formWarning &&
            data.result?.status?.warnings[0].code) &&
            // we show warnings on each estimation step and/or on last step of consumption/estimation
            (this.isLastStepConsumption || this.isLastStepEstimation)) {
              // save form warning code
              this.formWarning = (this.anfrage.userInput.warmwasser_verbrauch > 1 ? 'consumption.' : 'estimation.') + data.result.status.warnings[0].code;
              this.loading = false;
              return;
          }
          else {
            // reset form warnings
            this.formWarning = null;
            this.requestService.setAnfrage(this.anfrage);
            this.loading = false;
            // divert to correct next path or to result page (this.pathResultPage holds the correct path)
            this.router.navigateByUrl(this.portalService.currentPortalId + (this.pathNextStep ? this.pathNextStep : this.pathResultPage));
          }
        });
    });
  }

  /**
   * check if we need to show and end page for certain users.
   */
  isAdvisorEnd(): boolean {
    // case hausverwaltung
    if (this.anfrage.userInput?.nutzungstyp === WarmwassercheckConstants.NUTZUNGSTYP_HAUSVERWALTER) {
      return true;
    }

    //case hauseigentuemer in mfh
    if (this.anfrage.userInput?.nutzungstyp === WarmwassercheckConstants.NUTZUNGSTYP_HAUSEIGENTUEMER &&
      this.form.get('gebaeudetyp')?.value === WarmwassercheckConstants.GEBAEUDETYP_MFH) {
      return true;
    }

    return false;
  }

  /**
   * init help textkeys
   */
  initHelptextkeys() {
    // on step verbrauchsdaten/1 we overwrite the help textkey for mfh
    if(this.isMfh && this.currentStepName === WarmwassercheckConstants.FIRST_STEP_OF_VERBRAUCHSDATEN) {
      this.helpTextkey += '.mfh';
    }
    // versorgung_technik: we set the helptext depending on user input to warmwasser_bereietungstyp
    if(this.currentStepName === WarmwassercheckConstants.STEP_WASSERBEREITUNG_1 &&
        this.anfrage?.userInput?.warmwasser_bereitungstyp !== null) {
      this.helpTextkey += '.' + this.anfrage?.userInput?.warmwasser_bereitungstyp;
    }
    // versorgung_geraet: we set the helptext depending on user input to versorgung_technik
    if(this.currentStepName === WarmwassercheckConstants.STEP_WASSERBEREITUNG_2 &&
        this.anfrage?.userInput?.versorgung_technik !== null) {
      this.helpTextkey += '.' + this.anfrage?.userInput?.versorgung_technik;
    }
  }

  /**
   * set the step to correct result page
   */
  setResultSteps() {
    // first result: if no next step and we are on end of first input section
    if(this.isEarlyResult || (!this.steps?.nextStep && this.isLastStepGeraetedaten)) {
      // set next step as first result page ''
      this.steps.nextStep = new Step('ergebnis', WarmwassercheckConstants.PATH_ERGEBNIS);
    }
  }

  /**
   * update navigation dots when step is missing
   */
  updateNavigation(): void {
    const isVerbrauchBekannt = this.anfrage?.userInput?.warmwasser_verbrauch_bekannt === 'ja';
    const isEfh = this.anfrage?.userInput?.gebaeudetyp === WarmwassercheckConstants.GEBAEUDETYP_EFH;
    const isStepGeratedaten1 = this.currentStepName === WarmwassercheckConstants.STEP_GERAETEDATEN_1;
    const isStepWasserbereitung1 = this.currentStepName === WarmwassercheckConstants.STEP_WASSERBEREITUNG_1;
    let path = WarmwassercheckConstants.PATH_EINGABE;

    // mehrfamilienhaus
    if (this.isMfh && this.steps?.nextStep?.name !== 'ergebnis') {
      // gebaeudezentral on navigation step geraetedaten/1
      if (this.isGebaeudezentral && isStepGeratedaten1) {
        // set correct path if warmwasser_verbrauch is known
        path += isVerbrauchBekannt ? WarmwassercheckConstants.LAST_STEP_OF_CONSUMPTION_BEKANNT : WarmwassercheckConstants.LAST_STEP_OF_CONSUMPTION_GESCHAETZT;
        this.router.navigateByUrl(this.portalService.currentPortalId + path);
      }
      // gebaeudezentral on navigation step wasserbereitung/1
      if (this.isGebaeudezentral && isStepWasserbereitung1) {
        // set path to step of warmwasser_verbrauch_bekannt
        path += WarmwassercheckConstants.STEP_WW_BEKANNT;
        this.router.navigateByUrl(this.portalService.currentPortalId + path);
      }
      // wohnungszentral on navigation step wasserbereitung/1
      if (this.isWohnungszentral && isStepWasserbereitung1 && !this.formWarning) {
        // set path to step of warmwasser_verbrauch
        path += WarmwassercheckConstants.STEP_GERAETEDATEN_1;
        this.router.navigateByUrl(this.portalService.currentPortalId + path);
      }
    }

    // 1-2familienhaus & gebaeudezentral on navigation step geraetedaten/1
    if (isEfh && this.isGebaeudezentral && isStepGeratedaten1) {
      this.router.navigateByUrl((this.portalService.currentPortalId + path + WarmwassercheckConstants.STEP_GERAETEDATEN_2))
    }
  }
}
